<script setup>
import { ref } from 'vue'
import PortalDataTable from '@/components/DataTable/PortalDataTable.vue'
import { DataTableEndpointDefinition } from '@/models/datatable/DataTableEndpointDefinition.js'
import { Toaster } from '@/models/utils/Toaster.js'
import { Format } from '@/models/utils/FormatUtils.js'
import ObjectViewerModal from '@/components/Alerts/ObjectViewerModal.vue'
import CopyTextModal from '@/components/Alerts/CopyTextModal.vue'

const showCopyModal = ref(false)
const dialogTitle = ref('')
const plainDialogMessage = ref('')
const formattedDialogMessage = ref('')
const showObjectModal = ref(false)
const activeData = ref({})

const columns = [
	{ field: "id", title: "ID", sort: true, width: '1%' },
	{ field: "created_at", title: "Created", sort: true, width: '1%' },
	{ field: "updated_at", title: "Updated", sort: true, width: '1%' },
	{ field: "message", title: "Message", sort: false, minWidth: "400px" },
	{ field: "file", title: "File", sort: true, width: '1%' },
	{ field: "stack_trace", title: "Stack Trace", sort: false },
	{ field: "user_id", title: "User", sort: true, width: '1%' },
	{ field: "api_gateway_path", title: "API Gateway Path", sort: true, hide: true },
	{ field: "request_method", title: "Method", sort: true },
	{ field: "request_url", title: "URL", sort: true, width: '1%' },
	{ field: "request_payload", title: "Payload", sort: true },
	{ field: "request_host", title: "Host", sort: true, width: '400px' },
	{ field: "other_details", title: "Other Details", sort: false },
	{ field: "count", title: "Count", sort: true, width: '1%' },
	{ field: "status", title: "Status", sort: true, width: '1%' },
	{ field: "resolved_at", title: "Resolved At", sort: true, width: '1%' },
	{ field: "error_code", title: "Error Code", sort: true, width: '1%' },
	{ field: "type", title: "Type", sort: true, width: '1%' },
]

const endpoints = [
	new DataTableEndpointDefinition({
		endpointRoute: 'service-errors/api-gateway',
		endpointDisplayName: 'Api Gateway',
		initialSortColumn: 'updated_at',
		initialSortDirection: 'desc',
		searchPlaceholder: 'Search by Message, Exception, File, Path, Url, or Payload',
		fullTextSearch: true,
		columns: columns,
	}),
	new DataTableEndpointDefinition({
		endpointRoute: 'service-errors/app-service',
		endpointDisplayName: 'App/Data Service',
		initialSortColumn: 'updated_at',
		initialSortDirection: 'desc',
		searchPlaceholder: 'Search by Message or File',
		fullTextSearch: true,
		showCheckboxes: true,
		columns: columns,
	})
]

const table = ref(null)
const toaster = new Toaster()


function showStackTrace(title, message) {
	dialogTitle.value = title

	let formattedMessage = ''
	const traceLines = message.split('\n')

	for (const traceLine of traceLines) {
		const lineNumber = traceLine.slice(0, traceLine.indexOf(' '))
		const fileLocation = traceLine.slice(lineNumber.length, traceLine.indexOf(':') + 2)
		const functionMessage = traceLine.slice(traceLine.indexOf(': ') + 2)

		formattedMessage += `<span class="text-primary">${lineNumber}</span> <span class="font-semibold">${fileLocation}</span><span>${functionMessage}</span><br>`
	}

	plainDialogMessage.value = message
	formattedDialogMessage.value = formattedMessage

	showCopyModal.value = true
}

function showTextDialog(title, message) {
	dialogTitle.value = title
	plainDialogMessage.value = message
	formattedDialogMessage.value = message

	showCopyModal.value = true
}

function getOtherDetails(data) {
	return `Client Request ID: ${data.client_request_id}<br>Correlation ID: ${data.correlation_id}<br><br>${data.other_details}`
}

/**
 * Shows or hides the Object Viewer Modal
 * @param {boolean} p_show True to show modal
 * @param p_data
 */
function toggleObjectViewerModal(p_show, p_data)
{
	if (p_show)
	{
		showObjectModal.value = true
		activeData.value = p_data
	}
	else
	{
		showObjectModal.value = false;
	}
}

</script>

<template>
	<PortalDataTable
		ref="table"
		:endpoints="endpoints"
	>
		<template #created_at="data">
			{{ Format.date(data.value.created_at) }}
		</template>

		<template #updated_at="data">
			{{ Format.date(data.value.updated_at) }}
		</template>

		<template #environment="data">
			<span class="whitespace-nowrap">{{ data.value.environment }}</span>
		</template>

		<template #user_id="data">
			<span class="whitespace-nowrap">{{ data.value.user_name }} ({{ data.value.user_id }})</span>
		</template>

		<template #message="data">
			<div class="line-clamp-4 clickable-text overflow-ellipsis break-all" @click="showTextDialog('Message', data.value.message)">
				{{ data.value.message }}
			</div>
		</template>

		<template #file="data">
			<div class="whitespace-nowrap clickable-text" @click="showTextDialog('File', data.value.file)">
				...{{ data.value?.file?.slice(-20) }} (line #{{ data.value.line_number }})
			</div>
		</template>

		<template #stack_trace="data">
			<div class="line-clamp-1 clickable-text" @click="showStackTrace(data.value.exception_class, data.value.stack_trace)">
				Click to view
			</div>
		</template>

		<template #request_url="data">
			<div class="whitespace-nowrap clickable-text" @click="showTextDialog('URL', data.value.request_url)">
				...{{ data.value?.request_url?.slice(-30) }}
			</div>
		</template>

		<template #request_host="data">
			<div class="whitespace-nowrap clickable-text" @click="showTextDialog('Host', data.value.request_host)">
				{{ data.value.request_host }}
			</div>
		</template>

		<template #other_details="data">
			<div class="line-clamp-1 clickable-text" @click="showTextDialog('Other Details', getOtherDetails(data.value))">
				Click to view
			</div>
		</template>

		<template #request_payload="data">
			<div class="line-clamp-1 clickable-text"  @click="toggleObjectViewerModal(true, JSON.parse(data.value.request_payload))">
				Click to view
			</div>

<!--			<div class="line-clamp-1 clickable-text" @click="showTextDialog('Payload', JSON.stringify(JSON.parse(data.value.request_payload), null, 4).replaceAll('\n', '<br>'))">
				Click to view
			</div>-->
		</template>

		<template #count="data">
			<span class="whitespace-nowrap">{{ data.value.count }}</span>
		</template>
	</PortalDataTable>

	<ObjectViewerModal
		:data="activeData"
		:show="showObjectModal"
		:title="dialogTitle"
		@close="toggleObjectViewerModal(false)"
	/>

	<CopyTextModal
		:show="showCopyModal"
		:title="dialogTitle"
		:message="plainDialogMessage"
		:formattedMessage="formattedDialogMessage"
		@close="showCopyModal=false"
	/>
</template>